import { supabase } from '../supabaseClient'

interface Booking {
  bookable?: {
    name?: string
    type?: string
  }
}

class BookingService {
  getPaginatedBookings = async (
    page: number,
    term?: string,
    status?: string,
    bookable?: string,
    type?: string,
    supplierUuid?: string,
    sortOptions?: { sortField: string; sortDirection: 'asc' | 'desc' },
  ): Promise<any> => {
    try {
      const limit = 5
      const offset = (page - 1) * limit

      const { data: countData, error: countError } = await supabase.rpc('search_bookings_count', {
        _term: term ? `%${term}%` : null,
        _status: status || null,
        _bookable: bookable || null,
        _type: type || null,
        _supplier_uuid: supplierUuid || null,
      })
      if (countError) {
        console.error('Error fetching booking count:', countError)
      }

      const totalCount = countData ?? 0

      const { data: bookingsData, error: paginatedError } = await supabase.rpc(
        'search_bookings_paginated',
        {
          _term: term ? `%${term}%` : null,
          _status: status || null,
          _bookable: bookable || null,
          _type: type || null,
          _supplier_uuid: supplierUuid || null,
          _limit: limit,
          _offset: offset,
          _sort_field: sortOptions?.sortField || 'start_time',
          _sort_direction: sortOptions?.sortDirection || 'asc',
        },
      )

      if (paginatedError) {
        console.error('Error fetching paginated bookings:', paginatedError)
        return { data: [], count: 0 }
      }

      return {
        data: bookingsData || [],
        count: totalCount,
      }
    } catch (error) {
      console.error('Error in paginated booking:', { error })
    }
  }

  getBookingsById = async (id: string): Promise<any> => {
    try {
      const { data, error } = await supabase.from('bookings').select('*').eq('id', id).single()

      if (error) {
        console.error('Error fetching booking by ID:', error)
      }

      return data
    } catch (error) {
      console.error('Error fetching booking by ID:', { error })
    }
  }

  async getBookingsByBookableUUID(bookableUUID: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(
          `
          *,
          simple_profile!bookings_created_by_fkey(first_name, last_name)
        `,
        )
        .eq('bookable_uuid', bookableUUID)

      if (error) {
        console.error(`Error fetching bookings for bookableUUID ${bookableUUID}:`, error)
        return []
      }

      return data
    } catch (error) {
      console.error('Error in getBookingsByBookableUUID:', error)
      return []
    }
  }

  getBookings = async () => {
    const { data, error } = await supabase.from('bookings').select('*')
    return { data, error }
  }

  getDistinctBookablesNames = async (): Promise<(string | undefined)[]> => {
    try {
      const { data, error } = (await supabase
        .from('bookings')
        .select('bookable:bookable_uuid(name)')) as { data: Booking[]; error: any }

      if (error) {
        console.error('Error fetching bookable names:', error)
        return []
      }

      return Array.from(new Set(data.map((item) => item.bookable?.name).filter(Boolean)))
    } catch (error) {
      console.error('Error fetching distinct bookable names:', error)
      return []
    }
  }

  getDistinctBookablesTypes = async (): Promise<(string | undefined)[]> => {
    try {
      const { data, error } = (await supabase
        .from('bookings')
        .select('bookable:bookable_uuid(type)')) as { data: Booking[]; error: any }

      if (error) {
        console.error('Error fetching bookable types:', error)
        return []
      }

      return Array.from(new Set(data.map((item) => item.bookable?.type).filter(Boolean)))
    } catch (error) {
      console.error('Error fetching distinct bookable types:', error)
      return []
    }
  }
}

const bookingService = new BookingService()

export default bookingService
